// src/views/accueil/Feuille.jsx — LE CADRE COMMUN DES FEUILLES DE L'ACCUEIL,
// aligné VALEUR PAR VALEUR sur admin-proto.html (.voile/.feuille/.fHead/.fBody,
// arbitrage Dimitri 2026-08-26 : « le prototype fait foi »).
//
//   · voile rgba(35,50,64,.42)
//   · TÉLÉPHONE : ancrée en bas, rayon 18 18 0 0, poignée 36×4 #d5e3ee,
//     ombre 0 -8px 30px .22, max-height 82vh
//   · ORDINATEUR : CENTRÉE, 460 px, rayon 14 (pas collée en bas)
//   · en-tête : padding 11px 14px, FILET BAS 1px #e3e8ee, titre 14,5 px/600
//     POLICE SYSTÈME (pas Fraunces), suffixe gris 12 px possible
//   · fermer : carré 32×32, rayon 8, fond #f4f6f8, ✕ 16 px
//   · corps : padding 13px 14px 18px, défilement interne

import { useRef } from 'react'
import useEtroit from '../../lib/useEtroit'
import useEchapPourFermer from '../../lib/useEchapPourFermer'
import PortailFeuille from '../../components/PortailFeuille'

const BORD = '#e3e8ee'
const GRIS = '#5c6773'

export default function Feuille({ titre, suffixe, onFermer, enfants, testId, large = false }) {
  const etroit = useEtroit()
  useEchapPourFermer(onFermer, true)
  // LE GESTE VERS LE BAS (téléphone) : tiré depuis l'EN-TÊTE de plus de 70 px
  // vers le bas → la feuille se ferme, rien n'a changé. Le corps garde son
  // défilement — le geste ne vit que sur la poignée et le bandeau de titre.
  const toucheY = useRef(null)
  const surTouche = etroit ? {
    onTouchStart: (e) => { toucheY.current = e.touches[0].clientY },
    onTouchMove: (e) => {
      if (toucheY.current !== null && e.touches[0].clientY - toucheY.current > 70) {
        toucheY.current = null; onFermer()
      }
    },
    onTouchEnd: () => { toucheY.current = null },
  } : {}
  return (
    <PortailFeuille>
      <div role="dialog" aria-modal="true" aria-label={typeof titre === 'string' ? titre : 'Feuille'}
        data-testid={testId} onClick={onFermer}
        style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(35,50,64,.42)',
          display: 'flex',
          alignItems: etroit ? 'flex-end' : 'center',
          justifyContent: 'center' }}>
        <div onClick={(e) => e.stopPropagation()}
          style={{ background: '#fff', display: 'flex', flexDirection: 'column',
            maxHeight: '82vh', overflow: 'hidden',
            width: etroit ? '100%' : (large ? 'min(900px, 92vw)' : 460),
            borderRadius: etroit ? '18px 18px 0 0' : 14,
            boxShadow: '0 -8px 30px rgba(35,50,64,.22)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
          {etroit && (
            <div aria-hidden="true" {...surTouche} style={{ width: '100%', paddingTop: 8,
              display: 'flex', justifyContent: 'center', flexShrink: 0,
              touchAction: 'none' }}>
              <div style={{ width: 36, height: 4, background: '#d5e3ee', borderRadius: 2 }} />
            </div>
          )}
          <div {...surTouche} style={{ display: 'flex', alignItems: 'center', gap: 10,
            padding: '11px 14px', borderBottom: `1px solid ${BORD}`, flexShrink: 0 }}>
            <h3 style={{ fontSize: 14.5, fontWeight: 600, margin: 0, flex: 1, minWidth: 0,
              color: '#233240', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {titre}
              {suffixe && (
                <span style={{ fontWeight: 400, color: GRIS, fontSize: 12 }}> · {suffixe}</span>
              )}
            </h3>
            <button type="button" onClick={onFermer} aria-label="Fermer"
              data-testid={testId ? `${testId}-fermer` : undefined}
              style={{ width: 32, height: 32, borderRadius: 8, background: '#f4f6f8',
                color: GRIS, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, border: 'none', cursor: 'pointer', flexShrink: 0 }}>
              ✕
            </button>
          </div>
          <div style={{ padding: '13px 14px 18px', overflowY: 'auto', flex: 1,
            WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
            {enfants}
          </div>
        </div>
      </div>
    </PortailFeuille>
  )
}

/** La pastille d'agent du proto (.ag) : 44 px, initiales rondes 22 px,
 *  sélection = bord 2 px navy + fond #e8f2fa. */
export function PastilleAgent({ prenom, initiales, on, occupe, onClic, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClic} className="fbz-press"
      style={{ minHeight: 44, padding: '0 13px', borderRadius: 10,
        border: on ? '2px solid #3d5166' : `1px solid ${occupe ? '#d4a24c' : '#d5e3ee'}`,
        background: on ? '#e8f2fa' : occupe ? '#fdf7ee' : '#fff',
        fontSize: 12, color: on ? '#233240' : occupe ? '#8a5f19' : GRIS,
        fontWeight: on ? 600 : 400,
        display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
        fontFamily: 'inherit' }}>
      {initiales && (
        <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: '50%',
          background: '#e8f2fa', color: '#3d5166', fontSize: 9.5, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initiales}</span>
      )}
      {prenom}
    </button>
  )
}

/** L'en-tête de section du proto (inti) : carré 24×24 plein + libellé 11,5 px. */
export function EnTeteSection({ libelle, fond = '#d4a24c', couleur = '#8a5f19', glyphe = '✓' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 9px' }}>
      <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 7, background: fond,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, flexShrink: 0 }}>{glyphe}</span>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: couleur }}>{libelle}</span>
    </div>
  )
}

/** Les deux cases du créneau (hCase) : PLEIN #87b8e6 = annoncé, POINTILLÉS = contrat. */
export function CaseHeure({ libelle, heure, source, sur, testId }) {
  return (
    <div data-testid={testId} style={{ flex: 1, minWidth: 0, borderRadius: 12, padding: '10px 12px',
      ...(sur ? { background: '#87b8e6' } : { background: '#eaeff4', border: '1px dashed #c3cdd6' }) }}>
      <span style={{ display: 'block', fontSize: 10,
        color: sur ? 'rgba(255,255,255,.8)' : GRIS }}>{libelle}</span>
      <span style={{ display: 'block', fontSize: 21, fontWeight: 600, lineHeight: 1.15,
        marginTop: 2, color: sur ? '#fff' : GRIS }}>{heure}</span>
      <span style={{ display: 'block', fontSize: 9.5, marginTop: 1,
        color: sur ? 'rgba(255,255,255,.75)' : '#98a2ad' }}>{source}</span>
    </div>
  )
}

/** Le bouton d'action du proto (.bt) : 44 px, rayon 10, 12,5 px 600. */
export function BoutonProto({ libelle, ton = 'p', onClic, desactive, testId }) {
  const fonds = { p: '#b8412b', n: '#3d5166', v: '#7fc4a3', c: '#87b8e6', s: '#fff' }
  return (
    <button type="button" data-testid={testId} onClick={onClic} disabled={desactive}
      className="fbz-press fbz-tap"
      style={{ padding: '0 15px', minHeight: 44, borderRadius: 10, fontSize: 12.5,
        fontWeight: 600, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', gap: 7, fontFamily: 'inherit',
        background: fonds[ton] || fonds.p,
        color: ton === 's' ? '#3d5166' : '#fff',
        border: ton === 's' ? '1px solid #c3d5e4' : 'none',
        opacity: desactive ? 0.4 : 1, cursor: desactive ? 'default' : 'pointer' }}>
      {libelle}
    </button>
  )
}
